import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useForm, Controller } from 'react-hook-form';
import {
  Box,
  Alert,
  CircularProgress,
  TextField,
  Grid,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { AuthContext } from 'context/Auth';
import { db } from 'services/firebase';
import MasterChartBackButton from 'components/dashboard/healthRecords/MasterChartBackButton';
import DashboardPageHeader from 'components/common/DashboardPageHeader';
import DataLoadErrorAlert from 'components/common/DataLoadErrorAlert';

const OtherHistoryPage = () => {
  const { user } = useContext(AuthContext);

  const [fullHealthRecords, setFullHealthRecords] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [successMsg, setSuccessMsg] = useState(null);

  const { control, getValues, reset } = useForm({
    defaultValues: {
      smokingStatus: '',
      alcoholUse: '',
      drugUse: '',
      occupation: '',
      familyHistory: '',
    },
  });

  const load = useCallback(async () => {
    if (!user?.uid) return;
    setLoading(true);
    setLoadFailed(false);
    try {
      const doc = await db.collection('users').doc(user.uid).get();
      const hr = doc.data()?.healthRecords || {};
      setFullHealthRecords(hr);
      const other = hr.other || {};
      reset({
        smokingStatus: other.smokingStatus || '',
        alcoholUse: other.alcoholUse || '',
        drugUse: other.drugUse || '',
        occupation: other.occupation || '',
        familyHistory: other.familyHistory || '',
      });
    } catch {
      // Blank fields after a failed load look like an empty record, and saving
      // them would overwrite the real one, so the form stays hidden until a retry.
      setFullHealthRecords(null);
      setLoadFailed(true);
    } finally {
      setLoading(false);
    }
  }, [user?.uid, reset]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSave = useCallback(async () => {
    if (!user?.uid || saving || fullHealthRecords == null) return;
    setSaving(true);
    setError(null);
    try {
      const values = getValues();
      const merged = {
        ...(fullHealthRecords ?? {}),
        other: values,
        updatedAt: new Date().toISOString(),
      };
      await db
        .collection('users')
        .doc(user.uid)
        .set({ healthRecords: merged }, { merge: true });
      setFullHealthRecords(merged);
      setSuccessMsg('Saved.');
    } catch {
      setError('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  }, [user?.uid, saving, getValues, fullHealthRecords]);

  if (loading) {
    return (
      <div className="inner">
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      </div>
    );
  }

  return (
    <div className="inner">
      <DashboardPageHeader
        title="Other History"
        subtitle="Smoking, alcohol, work, and family history change what doctors check you for."
      />

      {loadFailed && (
        <DataLoadErrorAlert
          message="We couldn't load your history. Please retry before editing."
          onRetry={load}
        />
      )}
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {successMsg && (
        <Alert
          severity="success"
          onClose={() => setSuccessMsg(null)}
          sx={{ mb: 2 }}
        >
          {successMsg}
        </Alert>
      )}

      {!loadFailed && (
        <>
          {/* Flush with the heading like every other MasterChart page: no padded
              Paper, bold section headings. */}
          <Box component="section" sx={{ mb: 4 }}>
            <Typography variant="subtitle2" component="h3" fontWeight="bold" sx={{ mb: 2 }}>
              Social history
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="smokingStatus"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Smoking Status"
                      fullWidth
                      placeholder="e.g., Never smoker / Former smoker (quit 2015) / Current smoker"
                      helperText="Include current status and history if applicable."
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Controller
                  name="alcoholUse"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Alcohol Use"
                      fullWidth
                      placeholder="e.g., Occasional (1-2 drinks/week) / None"
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Controller
                  name="drugUse"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Drug Use"
                      fullWidth
                      placeholder="e.g., None / Marijuana (occasional)"
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Controller
                  name="occupation"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Occupation"
                      fullWidth
                      placeholder="e.g., Office worker / Construction / Healthcare"
                      helperText="Relevant for occupational exposures."
                    />
                  )}
                />
              </Grid>
            </Grid>
          </Box>

          <Box component="section" sx={{ mb: 3 }}>
            <Typography variant="subtitle2" component="h3" fontWeight="bold" sx={{ mb: 2 }}>
              Family history
            </Typography>
            <Box>
              <Controller
                name="familyHistory"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Family Medical History"
                    fullWidth
                    multiline
                    rows={5}
                    placeholder={
                      "List significant family medical history:\n• Father: Heart disease (age 55), Diabetes\n• Mother: Breast cancer (age 60), Hypertension\n• Grandparents: Stroke, Alzheimer's"
                    }
                    helperText="Include conditions, relationships, and age of onset if known."
                  />
                )}
              />
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 1 }}>
            <LoadingButton
              variant="contained"
              onClick={handleSave}
              loading={saving}
              disabled={saving}
            >
              Save
            </LoadingButton>
            <MasterChartBackButton />
          </Box>
        </>
      )}
    </div>
  );
};

export default OtherHistoryPage;
